// ============================================================================
// AFB-0075 / PR 3 — which documents one packet sends to one person, in order.
//
// Separate from envelope-lib.ts for the reason template-document.ts and
// template-bindings.ts are: that module imports pdfjs-dist at load time, which
// needs DOMMatrix, so it cannot be imported by a node-environment test AT ALL.
// Everything here is arithmetic over rows — and it is the arithmetic that
// decides which page of a signed document a signature box lands on, so it is
// the part of this PR that most needs pinning.
//
// Two rules carry the file:
//
//   1. A FACT WE DO NOT HAVE IS NOT A DEFAULT. Somebody with no state on file
//      does not "match the documents that go to everyone" — their state is
//      UNKNOWN. A blank state and a state that genuinely has no withholding
//      form (Texas) select the SAME five documents, so the difference is
//      invisible in the list and has to be SAID. 8 of the 26 people on file
//      have a null state, which makes this the ordinary case, not a corner one.
//
//   2. AN UNKNOWN PAGE COUNT MAKES THE WHOLE PACKET UNASSEMBLABLE. Offsets are
//      cumulative, so a missing count treated as zero does not cost one
//      document — it shifts every document after it, landing signature boxes on
//      the wrong page of something somebody then signs. templatePacketBlockers
//      refuses such a template at authoring time; this refuses it again here,
//      because a template can lose its document after it has joined a packet.
// ============================================================================

import { templateHasDocument } from './template-document';

/**
 * The worker classes `applies_to` is written in. Same three values as
 * hr-onboarding's OnboardingType, and deliberately the same spelling: the
 * checklist filters on these strings too, and two vocabularies for one idea is
 * how "Subcontractor" and "subcontractor" end up meaning different things.
 */
export const WORKER_CLASSES = ['W2', 'Partner', 'Subcontractor'] as const;

export type WorkerClass = (typeof WORKER_CLASSES)[number];

/**
 * One row of esign_packet_documents, as read.
 *
 * applies_to is jsonb, so what comes back is whatever was written; use
 * normalisePacketDocumentRow to get here from a raw row rather than casting.
 */
export interface PacketDocumentRow {
  link_id: string;
  packet_id: string;
  template_id: string;
  sort_order: number;
  /** Worker classes this document is limited to. NULL ⇒ everyone. */
  applies_to: string[] | null;
  /** Two-letter states this document is limited to. NULL ⇒ every state. */
  applies_to_states: string[] | null;
  is_required: boolean;
}

/** The same row before jsonb/text[] have been made trustworthy. */
export interface RawPacketDocumentRow {
  link_id: string;
  packet_id: string;
  template_id: string;
  sort_order: number;
  applies_to: unknown;
  applies_to_states: unknown;
  is_required: boolean;
}

/** What composition needs to know about a member template. */
export interface PacketDocumentTemplate {
  template_id: string;
  name: string;
  page_count: number | null;
  document_bytes: number | null;
}

/** The person the packet is being composed for. Either may be unknown. */
export interface PacketAudience {
  employment_type: string | null;
  state: string | null;
}

/** One selected document, placed in the assembled packet. */
export interface ComposedPacketDocument {
  link_id: string;
  template_id: string;
  /** The template's name, or the id when the template row is gone. */
  name: string;
  sort_order: number;
  is_required: boolean;
  /** Pages in this document. NULL means unknown, which is a blocker. */
  page_count: number | null;
  /**
   * 0-based page this document starts on. NULL when an EARLIER document's
   * length is unknown, because then this one's start genuinely is unknown too —
   * never 0, which is the value that quietly puts fields on page 1.
   */
  page_offset: number | null;
  /** The states that selected it, or null when it goes to every state. */
  states: string[] | null;
  /** The worker classes that selected it, or null when it goes to everyone. */
  classes: string[] | null;
  /** Why this document cannot be assembled, or null when it can. */
  blocker: string | null;
}

/** What a blank state costs on THIS packet, named rather than implied. */
export interface PacketStateGap {
  /** The state-specific documents that could not be chosen between. */
  skipped: Array<{ template_id: string; name: string; states: string[] }>;
  message: string;
}

export interface PacketComposition {
  /** The selected documents in sort_order. */
  documents: ComposedPacketDocument[];
  /** Pages in the assembled packet, or NULL when any length is unknown. */
  total_pages: number | null;
  /** False when at least one selected document cannot be assembled. */
  assemblable: boolean;
  /** Every reason it cannot be assembled, in plain sentences. */
  blockers: string[];
  /** Non-null only when the state is unknown AND this packet has state forms. */
  state_gap: PacketStateGap | null;
}

/** Case- and whitespace-insensitive comparison key. */
function fold(value: string): string {
  return value.trim().toUpperCase();
}

function isBlank(value: string | null | undefined): boolean {
  return (value ?? '').trim() === '';
}

/**
 * A restriction list from the database, made trustworthy.
 *
 * NULL, `[]`, `[""]` and a value that is not an array at all ALL read as "not
 * restricted", because that is what the column comment says an empty list
 * means and a jsonb column can hold any of them. Returning null rather than an
 * empty array gives "no restriction" exactly one representation, so a caller
 * cannot accidentally test `.length` on the wrong one.
 */
export function normaliseRestrictionList(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const out = value
    .filter((v): v is string => typeof v === 'string')
    .map((v) => v.trim())
    .filter((v) => v !== '');
  return out.length > 0 ? out : null;
}

/** One raw row, with its two restriction columns normalised. */
export function normalisePacketDocumentRow(row: RawPacketDocumentRow): PacketDocumentRow {
  return {
    link_id: row.link_id,
    packet_id: row.packet_id,
    template_id: row.template_id,
    sort_order: row.sort_order,
    applies_to: normaliseRestrictionList(row.applies_to),
    applies_to_states: normaliseRestrictionList(row.applies_to_states),
    is_required: row.is_required,
  };
}

/**
 * Does this document's worker-class restriction admit this person?
 *
 * An unrestricted document admits everyone INCLUDING somebody whose class is
 * unknown — "everyone" needs no fact to be true. A restricted one cannot admit
 * an unknown class, for the same reason as the state below.
 */
export function matchesWorkerClass(
  classes: string[] | null,
  employment_type: string | null,
): boolean {
  if (classes === null) return true;
  if (isBlank(employment_type)) return false;
  return classes.some((c) => fold(c) === fold(employment_type as string));
}

/**
 * Does this document's state restriction admit this person?
 *
 * A blank state matches NOTHING that is state-restricted. It is tempting to
 * treat "no state" as "the generic set", and the result even looks right — five
 * documents, which is what a Texas hire really gets. It is not right: nobody
 * decided this person has no state form, we just don't know where they live.
 * composePacket says so out loud; this function only refuses.
 */
export function matchesState(states: string[] | null, state: string | null): boolean {
  if (states === null) return true;
  if (isBlank(state)) return false;
  return states.some((s) => fold(s) === fold(state as string));
}

/** True when both restrictions on the row admit this person. */
export function documentApplies(row: PacketDocumentRow, audience: PacketAudience): boolean {
  return (
    matchesWorkerClass(row.applies_to, audience.employment_type) &&
    matchesState(row.applies_to_states, audience.state)
  );
}

/** sort_order, with link_id as the tie-break so the order is never arbitrary. */
function bySortOrder(a: PacketDocumentRow, b: PacketDocumentRow): number {
  return a.sort_order - b.sort_order || a.link_id.localeCompare(b.link_id);
}

/** Every row this person would receive, in the order they would receive it. */
export function selectPacketDocuments(
  rows: PacketDocumentRow[],
  audience: PacketAudience,
): PacketDocumentRow[] {
  return rows.filter((r) => documentApplies(r, audience)).sort(bySortOrder);
}

/**
 * Why this member cannot be assembled, or null.
 *
 * Page count is checked last and separately from the document because it is the
 * one that is silent: a template with no PDF is obvious the moment somebody
 * looks, whereas a null page_count produces a packet that assembles and is
 * wrong from that document onward.
 */
function memberBlocker(
  row: PacketDocumentRow,
  template: PacketDocumentTemplate | null,
): string | null {
  if (!template) {
    return (
      `${row.template_id} is on this packet but the template is gone or inactive, ` +
      `so its length is unknown — remove it or restore the template.`
    );
  }
  if (!templateHasDocument(template)) {
    return (
      `"${template.name}" has no saved document any more, so there is nothing to ` +
      `assemble. Re-attach its PDF or take it off the packet.`
    );
  }
  if ((template.page_count ?? 0) <= 0) {
    return (
      `"${template.name}" has an unknown page count, so every document after it ` +
      `would start on the wrong page. Re-save its PDF so the count can be read ` +
      `from it — the packet cannot be assembled until that is done.`
    );
  }
  return null;
}

/**
 * What a blank state costs ON THIS PACKET — nothing, if it holds no state forms.
 *
 * Filtered by worker class first on purpose: a Subcontractor packet that holds
 * no state-specific documents at all has no gap to report, and warning about
 * one there would train people to ignore the warning where it is real.
 */
function stateGap(
  rows: PacketDocumentRow[],
  templates: Map<string, PacketDocumentTemplate>,
  audience: PacketAudience,
): PacketStateGap | null {
  if (!isBlank(audience.state)) return null;
  const skipped = rows
    .filter((r) => matchesWorkerClass(r.applies_to, audience.employment_type))
    .filter((r): r is PacketDocumentRow & { applies_to_states: string[] } =>
      r.applies_to_states !== null)
    .sort(bySortOrder)
    .map((r) => ({
      template_id: r.template_id,
      name: templates.get(r.template_id)?.name ?? r.template_id,
      states: r.applies_to_states,
    }));
  if (skipped.length === 0) return null;
  const n = skipped.length;
  return {
    skipped,
    message:
      `This person's state is unknown, so none of the ${n} state-specific ` +
      `document${n === 1 ? '' : 's'} on this packet can be chosen — as composed, the ` +
      `packet would go out with NO state withholding form. That is not the same as a ` +
      `state that has none: a Texas hire really does receive the list below, and this ` +
      `person might not. Put a state on the employee record, or pick one here to see ` +
      `what they would actually be sent.`,
  };
}

/**
 * The whole answer for one packet and one person: which documents, in what
 * order, starting on which page, and everything standing in the way.
 *
 * page_offset is cumulative and stops being a number the moment a length is
 * unknown. Carrying the running total forward as if the unmeasured document
 * were zero pages long is the specific defect this function exists to prevent:
 * it does not drop a document, it moves every later document's fields onto the
 * wrong page of a document somebody then signs.
 */
export function composePacket(
  rows: PacketDocumentRow[],
  templates: PacketDocumentTemplate[],
  audience: PacketAudience,
): PacketComposition {
  const byId = new Map(templates.map((t) => [t.template_id, t]));
  const selected = selectPacketDocuments(rows, audience);

  let running = 0;
  let offsetKnown = true;

  const documents: ComposedPacketDocument[] = selected.map((row) => {
    const template = byId.get(row.template_id) ?? null;
    const pages = template?.page_count ?? 0;
    const page_count = pages > 0 ? pages : null;
    const page_offset = offsetKnown ? running : null;
    if (page_count === null) offsetKnown = false;
    else running += page_count;
    return {
      link_id: row.link_id,
      template_id: row.template_id,
      name: template?.name ?? row.template_id,
      sort_order: row.sort_order,
      is_required: row.is_required,
      page_count,
      page_offset,
      states: row.applies_to_states,
      classes: row.applies_to,
      blocker: memberBlocker(row, template),
    };
  });

  const blockers = documents
    .map((d) => d.blocker)
    .filter((b): b is string => b !== null);

  return {
    documents,
    total_pages: blockers.length === 0 ? running : null,
    assemblable: blockers.length === 0,
    blockers,
    state_gap: stateGap(rows, byId, audience),
  };
}

/** "pages 4–18" — 1-based and inclusive, the way a person counts pages. */
export function describePageSpan(doc: ComposedPacketDocument): string {
  if (doc.page_offset === null || doc.page_count === null) return 'pages unknown';
  const first = doc.page_offset + 1;
  const last = doc.page_offset + doc.page_count;
  return first === last ? `page ${first}` : `pages ${first}–${last}`;
}

/**
 * Move one document up or down, returning the rows renumbered from 1.
 *
 * Renumbers ALL of them rather than swapping the two sort_orders, because the
 * seeded packets are not guaranteed to be numbered contiguously and a swap
 * between 3 and 7 leaves the list looking unchanged.
 */
export function moveDocument(
  rows: PacketDocumentRow[],
  link_id: string,
  delta: -1 | 1,
): PacketDocumentRow[] {
  const ordered = [...rows].sort(bySortOrder);
  const from = ordered.findIndex((r) => r.link_id === link_id);
  const to = from + delta;
  if (from === -1 || to < 0 || to >= ordered.length) return ordered;
  const [moved] = ordered.splice(from, 1);
  ordered.splice(to, 0, moved);
  return ordered.map((r, i) => ({ ...r, sort_order: i + 1 }));
}
